import { api } from '@/internationalization/api-client'
import type {
  Meeting,
  MeetingAudio,
  MeetingDetail,
  MeetingScreenCapture,
  Paginated,
  ShareLink,
  SortOrder,
  UpcomingMeetings,
} from '@/types'

/** Sortable fields for GET /meetings. Mirrors the enum in docs/openapi.yaml. */
export type MeetingSort = 'startsAt' | 'title' | 'durationSeconds'

export interface MeetingListParams {
  /** Index signature so the params object can be used directly as a query key. */
  [key: string]: string | number | undefined
  q?: string
  status?: string
  /** Only meaningful alongside status='failed' — see MeetingFilters. */
  leaveReason?: string
  platform?: string
  /** A patron id, or 'none' for meetings no patron claims. */
  patron?: string
  series?: string
  /** Substring of the organiser's name or address. */
  organizer?: string
  /** Substring of any participant's name or address. */
  participant?: string
  /** Meeting date, YYYY-MM-DD. Alone it means that one day; with `dateTo`, the range. */
  dateFrom?: string
  dateTo?: string
  page?: number
  pageSize?: number
  sort?: MeetingSort
  order?: SortOrder
}

/**
 * The URL-cleaning rule behind MeetingsPage's `patch`: merge a partial change
 * in, then drop every param that is back at its 'all'/empty default. Pure
 * and exported so the URL round trip for a filter (leaveReason included) is
 * pinned by a test with no router involved.
 */
export function buildMeetingSearchParams(
  current: MeetingListParams,
  next: Partial<MeetingListParams>,
): URLSearchParams {
  const merged = { ...current, ...next }
  const clean = new URLSearchParams()

  if (merged.q) clean.set('q', String(merged.q))
  if (merged.status && merged.status !== 'all') {
    clean.set('status', merged.status)
  }
  if (merged.leaveReason && merged.leaveReason !== 'all') {
    clean.set('leaveReason', merged.leaveReason)
  }
  if (merged.platform && merged.platform !== 'all') {
    clean.set('platform', merged.platform)
  }
  if (merged.patron && merged.patron !== 'all') {
    clean.set('patron', merged.patron)
  }
  if (merged.organizer) clean.set('organizer', String(merged.organizer))
  if (merged.participant) clean.set('participant', String(merged.participant))
  if (merged.dateFrom) clean.set('dateFrom', merged.dateFrom)
  if (merged.dateTo) clean.set('dateTo', merged.dateTo)
  if (merged.page && merged.page > 1) {
    clean.set('page', String(merged.page))
  }

  return clean
}

/** The local midnight starting `day` (YYYY-MM-DD), `offsetDays` later. */
function localMidnight(day: string, offsetDays = 0): Date | null {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d + offsetDays)
}

/**
 * The half-open instant window for a chosen day or range, in the VIEWER's
 * timezone: "21 September" means their 21 September, not UTC's. A lone From
 * is open ended (on or after that day) and a lone To is open at the start (on
 * or before it); two are the inclusive range, in whichever order they were
 * picked, and equal dates are that single day. Computed in the browser, like
 * the actions page's `today`, because only the browser knows which day the
 * person means.
 */
export function meetingDateWindow(
  dateFrom?: string,
  dateTo?: string,
): { startsFrom?: string; startsBefore?: string } | null {
  if (!dateFrom && !dateTo) return null
  if (dateFrom && dateTo) {
    const [from, to] = dateFrom <= dateTo ? [dateFrom, dateTo] : [dateTo, dateFrom]
    const start = localMidnight(from)
    const end = localMidnight(to, 1)
    if (!start || !end) return null
    return { startsFrom: start.toISOString(), startsBefore: end.toISOString() }
  }
  if (dateFrom) {
    const start = localMidnight(dateFrom)
    return start ? { startsFrom: start.toISOString() } : null
  }
  const end = localMidnight(dateTo!, 1)
  return end ? { startsBefore: end.toISOString() } : null
}

export interface RecordMeetingRequest {
  platform: 'teams' | 'meet'
  meetingUrl: string
  /** Optional — a pasted link carries no calendar subject to fall back on. */
  title?: string
}

export interface RecordMeetingResponse extends RecordMeetingRequest {
  status: 'requested'
}

/** One uploaded chunk of "Record now" audio, as reported on completion. */
export interface DeviceAudioChunk {
  index: number
  sizeBytes: number
  /** Wall clock (epoch SECONDS) at the chunk's first sample. */
  startedAt: number
  rms: number
}

/**
 * The feature's only contact with the network. Screens never call `api`
 * directly - when the backend renames a field, this file is the diff.
 */
export const meetingsApi = {
  list: ({ dateFrom, dateTo, ...params }: MeetingListParams) =>
    api.get<Paginated<Meeting>>('/meetings', {
      query: { ...params, ...meetingDateWindow(dateFrom, dateTo) },
    }),
  record: (body: RecordMeetingRequest) =>
    api.post<RecordMeetingResponse>('/meetings/record', body),
  /**
   * "Record now": the browser records, no bot joins anything. Create the
   * meeting, upload 16 kHz mono PCM chunks as they are captured, then
   * complete -- which starts the normal transcription and summary.
   */
  deviceRecording: {
    start: (title?: string) =>
      api.post<{ meetingId: string; maxSeconds: number }>(
        '/meetings/device-recordings',
        title ? { title } : {},
      ),
    uploadChunk: (meetingId: string, index: number, pcm: ArrayBuffer) =>
      api.putBinary(`/meetings/${meetingId}/device-audio/${index}`, pcm),
    complete: (meetingId: string, chunks: DeviceAudioChunk[], stoppedAt: number) =>
      api.post<{ status: 'processing' }>(`/meetings/${meetingId}/device-audio/complete`, {
        chunks,
        stoppedAt,
      }),
    /** Throw away an unfinished recording so it stops being listed as live. */
    discard: (meetingId: string) =>
      api.delete<void>(`/meetings/${meetingId}/device-recording`),
  },
  upcoming: () => api.get<UpcomingMeetings>('/meetings/upcoming'),
  /**
   * Re-reads the bot mailbox's calendar NOW, past the server's cache. A POST
   * because it makes an outbound Graph request on demand — it is an action, not
   * a variation on a cacheable read.
   */
  refreshUpcoming: () => api.post<UpcomingMeetings>('/meetings/upcoming/refresh'),
  detail: (id: string) => api.get<MeetingDetail>(`/meetings/${id}`),
  /**
   * WORKS ON A MEETING THAT HAS NO ROW YET, which is the point: the scheduler
   * writes one five minutes before the start, and the only useful moment to
   * decide this is before that. The id is the same one "Up next" already shows
   * for a calendar entry.
   */
  screenCapture: {
    get: (id: string) => api.get<MeetingScreenCapture>(`/meetings/${id}/screen-capture`),
    /** `null` means INHERIT — clear the override and follow the workspace. */
    set: (id: string, screenCapture: boolean | null) =>
      api.put<MeetingScreenCapture>(`/meetings/${id}/screen-capture`, { screenCapture }),
  },
  /** Creates (or returns) the public share link. The token is a bearer credential. */
  share: (id: string) => api.post<ShareLink>(`/meetings/${id}/share`),
  unshare: (id: string) => api.delete<void>(`/meetings/${id}/share`),
  reprocess: (id: string) => api.post<{ status: string }>(`/meetings/${id}/reprocess`),
  /**
   * TEMPORARY: re-run ASR repair over a meeting that is already transcribed.
   *
   * Unlike `reprocess` this does NOT re-transcribe and does NOT re-summarise —
   * it writes only the cleaned transcript, so nothing a reader is looking at is
   * replaced. Remove with the endpoint once the cleaning has been judged.
   */
  clean: (id: string) =>
    api.post<{ status: string; taskArn: string | null }>(`/meetings/${id}/clean`),
  /**
   * A playable source for this meeting's recording.
   *
   * JSON RATHER THAN AN `<audio src>` STRAIGHT AT THE API, because the access
   * token lives in memory and travels as a header — an `<audio>` element
   * cannot send one, so pointing it at `/meetings/{id}/audio` would be an
   * unauthenticated request answered 401. The URL this returns is presigned
   * and needs no header, which is what makes it playable AND seekable: S3
   * answers Range requests, so dragging the scrubber works.
   *
   * 404 `audio_unavailable` is an ordinary answer (never recorded, or
   * recorded and not yet transcribed); 503 `audio_not_configured` means no
   * meeting on this deployment can be played. The player renders those two
   * differently.
   */
  audio: (id: string) => api.get<MeetingAudio>(`/meetings/${id}/audio`),
  /**
   * Send the recorder back into a meeting whose host refused it.
   *
   * 202, NOT 200: nothing has joined anything yet. The launch claim is
   * dropped and the scheduler launches on its next sub-tick, ~15-20 s later,
   * with the recorder in the lobby about a minute after that. Reporting this
   * as done would send somebody looking for a bot that is not there.
   *
   * WHETHER IT WOULD BE ACCEPTED IS `meeting.canRejoin`, from the server.
   * The rule has five clauses and two of them -- a request already in flight,
   * and the per-meeting cap -- are not in the meeting payload at all, so
   * deriving it here from `leaveReason` would offer a retry the API refuses
   * on exactly the meetings where somebody has already pressed it once.
   */
  rejoin: (id: string) => api.post<{ status: string }>(`/meetings/${id}/rejoin`),
  remove: (id: string) => api.delete<void>(`/meetings/${id}`),
  /**
   * The Meeting Outcome Report PDF: metadata, the existing summary, decisions
   * and action items -- never the transcript or the participant list. A blob
   * rather than a plain link because the access token travels as a header
   * (see `sendBlob`), so an `<a href>` straight at the API would be
   * unauthenticated.
   *
   * `tz` is this browser's own resolved zone, sent so the PDF's Date/Time
   * match what the SPA is showing right now rather than raw UTC. The API
   * falls back to a labelled UTC on its own if this is ever unavailable, so
   * nothing here needs to guess a timezone or retry.
   */
  exportPdf: (id: string) =>
    api.getBlob(`/meetings/${id}/export`, {
      query: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
    }),
}
